
import { defineComponent, PropType, reactive, onBeforeUnmount, Ref, ref, watch } from 'vue'

// components
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FileLoader from '@/common/components/UI/TheButtonFileLoader/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// module components
import ReceiversListItem from '@/modules/CreateOrderModule/components/ReceiversListItem/index.vue'
import TemplatesActions from '@/modules/CreateOrderModule/components/TemplatesActions/index.vue'

// types
import { ReceiverPayloadT, ReceiverT } from '@/core/types/receivers.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { receivers } from '@/core/realisations/receivers.realisation'

// composable
import { useDebounce } from 'vue-composable'
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        loader: Loader,
        'the-button': TheButton,
        'file-loader': FileLoader,
        'the-input': TheInput,
        //
        'receivers-list-item': ReceiversListItem,
        'templates-actions': TemplatesActions,
    },
    props: {
        senderId: {
            type: Number,
            default: null,
        },
        cityId: {
            type: Number,
            default: null,
        },
        companyId: {
            type: Number,
            default: null,
        },
        opened: {
            type: Boolean,
            default: false,
        },
        stepNumber: {
            type: Number,
            default: 0,
        },
        receivers: {
            type: Array as PropType<ReceiverT[]>,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        selectedReceiversIds: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        withExcel: {
            type: Boolean,
            default: false,
        },
        excelLoading: {
            type: Boolean,
            defaut: false,
        },
        hint: {
            type: String,
            defaut: null,
        },
    },
    emits: ['reset', 'submit', 'receiver-updated', 'create-from-excel'],
    setup(props, { emit }) {
        const state = reactive({
            search_query: null as string | null,
        })

        const data_state = reactive({
            receivers: new DataState<ReceiverT>({}),
        })

        const loadReceivers = async (page: number) => {
            if (!props.companyId) return

            data_state.receivers.pagination.current_page = page

            try {
                data_state.receivers.is_loading = true

                const response = await receivers.getListWithoutCityId(
                    page,
                    props.companyId,
                    props.cityId
                )
                data_state.receivers.list = response.data.receivers.filter(
                    (r) => !props.receivers.find((r_p) => r_p.id === r.id)
                )
                data_state.receivers.pagination.total_pages = response.data.total_pages

                data_state.receivers.is_failed = false
                data_state.receivers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.receivers.failure.catchError(error)

                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_failed = true
                }
            } finally {
                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_loading = false
                }
            }
        }

        // infinite scroll
        const loadMoreReceivers = async (page: number) => {
            if (page > data_state.receivers.pagination.total_pages) return
            data_state.receivers.pagination.current_page = page

            try {
                data_state.receivers.is_loading = true

                const response = await receivers.getListWithoutCityId(
                    page,
                    props.companyId,
                    props.cityId
                )
                data_state.receivers.list = data_state.receivers.list.concat(
                    response.data.receivers.filter(
                        (r) => !props.receivers.find((r_p) => r_p.id === r.id)
                    )
                )
                data_state.receivers.pagination.total_pages = response.data.total_pages

                data_state.receivers.is_failed = false
                data_state.receivers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.receivers.failure.catchError(error)

                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_failed = true
                }
            } finally {
                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_loading = false
                }
            }
        }

        const templatewindow: Ref<HTMLElement | null> = ref(null)

        const handleScroll = () => {
            if (!templatewindow.value || data_state.receivers.is_loading) return

            const scrollHeight = Math.floor(templatewindow.value.scrollHeight)
            const scrollTop = Math.floor(templatewindow.value.scrollTop)
            const offsetHeight = Math.floor(templatewindow.value.offsetHeight)

            if (scrollTop >= scrollHeight - offsetHeight) {
                loadMoreReceivers(data_state.receivers.pagination.current_page + 1)
            }
        }

        onBeforeUnmount(() => {
            if (!templatewindow.value) return
            templatewindow.value.removeEventListener('scroll', handleScroll)
        })

        const searchReceiver = useDebounce(async () => {
            data_state.receivers.pagination.current_page = 1
            data_state.receivers.pagination.total_pages = 1
            data_state.receivers.list = []

            if (!state.search_query || state.search_query === '') {
                loadReceivers(1)
                return
            }

            try {
                data_state.receivers.is_loading = true

                const response = await receivers.searchReceiver({
                    param: state.search_query,
                    exact_city_id: props.cityId,
                    company_id: props.companyId,
                })

                data_state.receivers.list = response.data.receivers.filter(
                    (r) => !props.receivers.find((r_p) => r_p.id === r.id)
                )
                data_state.receivers.is_failed = false
                data_state.receivers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.receivers.failure.catchError(error)

                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_failed = true
                }
            } finally {
                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_loading = false
                }
            }
        }, 800)

        watch(
            () => props.opened,
            (opened) => {
                if (opened) {
                    loadReceivers(1)
                }

                if (!opened) {
                    state.search_query = null
                    data_state.receivers.pagination.current_page = 1
                    data_state.receivers.pagination.total_pages = 1
                    data_state.receivers.list = []
                }
            }
        )

        watch(
            () => templatewindow.value,
            (templatewindow_newest) => {
                if (templatewindow_newest) {
                    if (!templatewindow.value) return
                    templatewindow.value.addEventListener('scroll', handleScroll)
                }
            }
        )

        const onReceiverSelect = (receiver: ReceiverT) => {
            const index = data_state.receivers.list.findIndex(
                (receiver) => receiver.id === receiver.id
            )
            data_state.receivers.list.splice(index, 1)

            emit('submit', { ...receiver })
        }

        const emitReset = (index: number) => {
            emit('reset', index)

            data_state.receivers.pagination.current_page = 1
            data_state.receivers.pagination.total_pages = 1
            data_state.receivers.list = []

            loadReceivers(1)
        }
        //
        const { actions_state, startCreate, startUpdate } = useActions<ReceiverT>()
        const notifications = useNotifications()

        const onReceiverCreate = async (payload: ReceiverPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                await receivers.createReceiver(payload)
                notifications.notify({ title: 'Получатель создан', type: 'success' })

                await loadReceivers(1)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при создании получателя' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onReceiverUpdate = async (payload: ReceiverPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                if (!payload.id) return

                if (props.isUpdate) {
                    await receivers.updateReceiverInOrder(payload.id, payload)
                    emit('receiver-updated', { receiver: payload })
                } else {
                    await receivers.updateReceiver(payload.id, payload)
                }

                notifications.notify({ title: 'Получатель отредактирован', type: 'success' })

                await loadReceivers(1)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при редактировании получателя' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onReceiverDeleted = () => {
            loadReceivers(data_state.receivers.pagination.current_page)
        }

        const emitCreateFromExcel = (files: File[]) => {
            if (!files.length) return
            emit('create-from-excel', files[0])
        }

        return {
            state,
            data_state,
            loadReceivers,
            searchReceiver,
            onReceiverSelect,
            emitReset,
            actions_state,
            startCreate,
            startUpdate,
            onReceiverCreate,
            onReceiverUpdate,
            onReceiverDeleted,
            templatewindow,
            emitCreateFromExcel,
        }
    },
})
